import { observer } from 'mobx-react-lite'
import type { ReactNode } from 'react'
import { Badge } from '@/components/ui/Badge'
import { env } from '@/config/env'
import { i18n, LOCALE_NAMES, useT, type Locale } from '@/i18n'
import { LogoMark } from '@/layout/Logo'
import { cn } from '@/lib/cn'
import { CONTENT_LOCALES } from '@/types/api'

const SHORT_NAMES: Record<Locale, string> = { en: 'EN', ru: 'RU', uz: 'UZ', cyr: 'ЎЗ' }

/** Language is chosen before sign-in, so the whole flow reads in it. */
const LanguagePicker = observer(function LanguagePicker() {
  const t = useT()
  return (
    <div role="radiogroup" aria-label={t('common.language')} className="flex gap-0.5 rounded-[calc(12px*var(--radius-scale))] bg-hover p-0.5">
      {CONTENT_LOCALES.map((locale) => {
        const active = i18n.locale === locale
        return (
          <button
            key={locale}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={LOCALE_NAMES[locale]}
            onClick={() => void i18n.setLocale(locale)}
            className={cn(
              'h-7.5 rounded-[calc(10px*var(--radius-scale))] px-2.5 text-[length:calc(12.5px*var(--text-scale))] font-medium transition-[background-color,color,box-shadow] duration-[var(--dur-base)]',
              active ? 'bg-glass-solid text-fg shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_var(--glass-highlight)]' : 'text-muted hover:text-fg',
            )}
          >
            <span className="hidden sm:inline">{LOCALE_NAMES[locale]}</span>
            <span className="sm:hidden">{SHORT_NAMES[locale]}</span>
          </button>
        )
      })}
    </div>
  )
})

/** Sign-in and password reset: the form alone, in a glass card at the centre of the page. */
export function AuthLayout({ title, description, children, footer }: { title: string; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  const t = useT()
  return (
    <div className="flex min-h-dvh flex-col items-center px-4 py-4 sm:px-6 sm:py-5">
      <div className="flex w-full justify-end">
        <LanguagePicker />
      </div>

      <main className="flex w-full max-w-[460px] flex-1 flex-col justify-center py-10">
        <div className="animate-fade-in mb-6 flex items-center justify-center gap-3">
          <LogoMark className="h-14" />
          <div className="leading-tight">
            <div className="text-[length:calc(15px*var(--text-scale))] font-semibold tracking-[-0.015em] text-fg">{t('layout.brand.name')}</div>
            <div className="text-[length:calc(12px*var(--text-scale))] text-muted">{t('layout.brand.tagline')}</div>
          </div>
        </div>

        <div className="glass glass-sheen rounded-[calc(28px*var(--radius-scale))] px-6 py-7 sm:px-9 sm:py-9">
          <div key={title} className="animate-rise-in">
            <h1 className="text-[length:calc(26px*var(--text-scale))] font-semibold leading-tight tracking-[-0.028em] text-fg">{title}</h1>
            {description && <p className="mt-2 text-[length:calc(14.5px*var(--text-scale))] leading-relaxed text-muted">{description}</p>}
          </div>
          <div className="animate-rise-in mt-7 [animation-delay:60ms]">{children}</div>
          {footer && <div className="animate-fade-in mt-7 border-t border-line pt-6 text-[length:calc(13.5px*var(--text-scale))] text-muted [animation-delay:120ms]">{footer}</div>}
        </div>
      </main>

      <footer className="flex items-center justify-center gap-2 text-[length:calc(12px*var(--text-scale))] text-subtle">
        {!env.isProduction && (
          <Badge tone="warning" className="h-5 min-w-0 px-1.5 text-[length:calc(10.5px*var(--text-scale))] uppercase tracking-[0.04em]">
            {env.appEnv}
          </Badge>
        )}
        <span>{t('auth.hero.rights', { year: new Date().getFullYear() })}</span>
      </footer>
    </div>
  )
}
